/**
 * The themes a reader of the HeyDay News app chooses from, in its one Settings:
 * a theme for light and one for dark, worn by every screen of the app and by
 * HeyDex in its tab. Each id is a HeyDex theme id, so the app tells HeyDex which
 * theme to wear over the bridge (`./heydex`). A theme is one set of colours on
 * both sides: each palette here is its HeyDex theme's colours read into the
 * app's tokens, role for role, and each swatch is HeyDex's preview, held equal by
 * HeyDex's tests. HeyDay, the default, is heyday.news's own palette.
 */

/** The colours every screen of the app is drawn in, as `--color-<token>`. */
export const PALETTE_TOKENS = ["paper", "paper-2", "card", "ink", "ink-2", "slate", "rule", "rule-2", "red", "on-red", "red-ink", "amber", "amber-ink", "green", "desk"] as const;

export type PaletteToken = (typeof PALETTE_TOKENS)[number];
/** A palette's colours, `#RRGGBB`. */
export type Palette = Readonly<Record<PaletteToken, string>>;

export type ThemeMode = "light" | "dark";

/**
 * A theme's swatch in Settings, as HeyDex draws it: its page, the accent that
 * glows from one corner and the action colour that tints the other.
 */
export type Orb = Readonly<{ canvas: string; accent: string; action: string }>;

/** How a swatch is lit in each mode: HeyDex's `THEME_PREVIEW_RENDER_SPECS`, value for value. */
export const ORB_LIGHTING = {
  light: {
    baseTarget: "#ffffff",
    baseWeight: 0.8,
    accent: { center: [0.72, 0.22], middleOffset: 0.28, middleOpacity: 0.72, endOffset: 0.58 },
    action: { center: [0.18, 0.82], startOpacity: 0.45, endOffset: 0.55 },
    scale: 1.1,
    blurAt56Px: 3,
  },
  dark: {
    baseTarget: "#09090b",
    baseWeight: 0.8,
    accent: { center: [0.28, 0.78], middleOffset: 0.28, middleOpacity: 0.62, endOffset: 0.58 },
    action: { center: [0.82, 0.18], startOpacity: 0.45, endOffset: 0.55 },
    scale: 1.1,
    blurAt56Px: 3,
  },
} as const;


/**
 * heyday.news's own palette (apps/site/styles/globals.css); the desk is the site's
 * black in both modes, and the white the site sets on red is `on-red`.
 */
const HEYDAY_LIGHT: Palette = {
  paper: "#FFFFFF", "paper-2": "#F5F5F7", card: "#F5F5F7", ink: "#1D1D1F", "ink-2": "#424245", slate: "#6E6E73", rule: "#E8E8ED", "rule-2": "#D2D2D7",
  red: "#D70015", "on-red": "#FFFFFF", "red-ink": "#B3170F", amber: "#D9992B", "amber-ink": "#8A5F0E", green: "#1E7A47", desk: "#000000",
};
const HEYDAY_DARK: Palette = {
  paper: "#000000", "paper-2": "#0C0C0D", card: "#161618", ink: "#F5F5F7", "ink-2": "#C7C7CC", slate: "#8E8E93", rule: "#232325", "rule-2": "#3A3A3C",
  red: "#FF453A", "on-red": "#FFFFFF", "red-ink": "#FF6B61", amber: "#FFB340", "amber-ink": "#FFC870", green: "#4DD68A", desk: "#000000",
};

/** In Settings' order. */
export const APP_THEMES = [
  {
    id: "heyday",
    label: "HeyDay",
    light: HEYDAY_LIGHT,
    dark: HEYDAY_DARK,
    orb: { light: { canvas: "#FFFFFF", accent: "#D70015", action: "#1D1D1F" }, dark: { canvas: "#000000", accent: "#FF453A", action: "#F5F5F7" } },
  },
  {
    id: "heydex",
    label: "HeyDex",
    light: { paper: "#FCFCFC", "paper-2": "#FFFFFF", card: "#FCFCFC", ink: "#27272A", "ink-2": "#27272A", slate: "#71717B", rule: "#E4E4E7", "rule-2": "#D4D4D8", red: "#1B4ED8", "on-red": "#FFFFFF", "red-ink": "#1B4ED8", amber: "#FE9A00", "amber-ink": "#BB4D00", green: "#1B4ED8", desk: "#000000" },
    dark: { paper: "#0A0A0A", "paper-2": "#111111", card: "#111111", ink: "#F5F5F5", "ink-2": "#F5F5F5", slate: "#818181", rule: "#191919", "rule-2": "#1E1E1E", red: "#346BF1", "on-red": "#FFFFFF", "red-ink": "#346BF1", amber: "#FE9A00", "amber-ink": "#FFB900", green: "#346BF1", desk: "#000000" },
    orb: { light: { canvas: "#FCFCFC", accent: "#F4F4F5", action: "#4F46E5" }, dark: { canvas: "#0A0A0A", accent: "#1C1C1F", action: "#8B9CFF" } },
  },
  {
    id: "heydex-chat",
    label: "Rose",
    light: { paper: "#FDF7FD", "paper-2": "#F3E6F5", card: "#FDFAFD", ink: "#501854", "ink-2": "#77347C", slate: "#AC1668", rule: "#EEE1ED", "rule-2": "#E7C1DC", red: "#DB2777", "on-red": "#FFFFFF", "red-ink": "#DB2777", amber: "#F59E0B", "amber-ink": "#B05109", green: "#DB2777", desk: "#171018" },
    dark: { paper: "#1F1A24", "paper-2": "#362D3D", card: "#2C2631", ink: "#F9F8FB", "ink-2": "#D4C7E1", slate: "#E7D0DD", rule: "#27242C", "rule-2": "#302029", red: "#A3004C", "on-red": "#FBD0E8", "red-ink": "#DB2777", amber: "#F59E0B", "amber-ink": "#FBBF24", green: "#A3004C", desk: "#171018" },
    orb: { light: { canvas: "#FDF7FD", accent: "#DB2777", action: "#DB2777" }, dark: { canvas: "#1F1A24", accent: "#A3004C", action: "#A3004C" } },
  },
  {
    id: "grove",
    label: "Grove",
    light: { paper: "#F3F7F4", "paper-2": "#E2EDE7", card: "#ECEFED", ink: "#241523", "ink-2": "#241523", slate: "#746C73", rule: "#CBD5D1", "rule-2": "#BECBC5", red: "#1B7D50", "on-red": "#FFFAFF", "red-ink": "#1B7D50", amber: "#FE9A00", "amber-ink": "#B64A00", green: "#1B7D50", desk: "#21362B" },
    dark: { paper: "#1B2821", "paper-2": "#2A4B39", card: "#36413C", ink: "#FFFAFF", "ink-2": "#FFFAFF", slate: "#919595", rule: "#415F4F", "rule-2": "#4F725F", red: "#69D69A", "on-red": "#241523", "red-ink": "#69D69A", amber: "#FE9A00", "amber-ink": "#FFB900", green: "#69D69A", desk: "#21362B" },
    orb: { light: { canvas: "#F3F7F4", accent: "#1B7D50", action: "#8F6410" }, dark: { canvas: "#1B2821", accent: "#69D69A", action: "#E3B34E" } },
  },
  {
    id: "ocean",
    label: "Ocean",
    light: { paper: "#F5F7F8", "paper-2": "#E4ECF2", card: "#EDEFF1", ink: "#241523", "ink-2": "#241523", slate: "#746C75", rule: "#CDD4DC", "rule-2": "#C0C9D4", red: "#2672AF", "on-red": "#FFFAFF", "red-ink": "#2672AF", amber: "#FE9A00", "amber-ink": "#B74B00", green: "#2672AF", desk: "#1E2D3B" },
    dark: { paper: "#17212B", "paper-2": "#293F52", card: "#333B45", ink: "#FFFAFF", "ink-2": "#FFFAFF", slate: "#8D8F97", rule: "#405567", "rule-2": "#4F677B", red: "#70B9EE", "on-red": "#241523", "red-ink": "#70B9EE", amber: "#FE9A00", "amber-ink": "#FFB900", green: "#70B9EE", desk: "#1E2D3B" },
    orb: { light: { canvas: "#F5F7F8", accent: "#2672AF", action: "#0A6F75" }, dark: { canvas: "#17212B", accent: "#70B9EE", action: "#5BD0D6" } },
  },
  {
    id: "ember",
    label: "Ember",
    light: { paper: "#F9F7F5", "paper-2": "#F3EAE5", card: "#F1EFEE", ink: "#241523", "ink-2": "#241523", slate: "#766C74", rule: "#DDD2CE", "rule-2": "#D4C6C1", red: "#AE552A", "on-red": "#FFFAFF", "red-ink": "#AE552A", amber: "#FE9A00", "amber-ink": "#B84B00", green: "#AE552A", desk: "#39281F" },
    dark: { paper: "#291E1A", "paper-2": "#513728", card: "#433835", ink: "#FFFAFF", "ink-2": "#FFFAFF", slate: "#968E8F", rule: "#664C3F", "rule-2": "#7A5D4D", red: "#F09A64", "on-red": "#241523", "red-ink": "#F09A64", amber: "#FE9A00", "amber-ink": "#FFB900", green: "#F09A64", desk: "#39281F" },
    orb: { light: { canvas: "#F9F7F5", accent: "#AE552A", action: "#B23535" }, dark: { canvas: "#291E1A", accent: "#F09A64", action: "#F78A7A" } },
  },
  {
    id: "iris",
    label: "Iris",
    light: { paper: "#F8F7F9", "paper-2": "#EDEAF4", card: "#F0EFF2", ink: "#241523", "ink-2": "#241523", slate: "#766C76", rule: "#D6D1DE", "rule-2": "#CCC5D6", red: "#7253B9", "on-red": "#FFFAFF", "red-ink": "#7253B9", amber: "#FE9A00", "amber-ink": "#B84B00", green: "#7253B9", desk: "#272139" },
    dark: { paper: "#1D1929", "paper-2": "#362D51", card: "#383443", ink: "#FFFAFF", "ink-2": "#FFFAFF", slate: "#8E8A95", rule: "#4D4366", "rule-2": "#5D527B", red: "#9D7DF2", "on-red": "#241523", "red-ink": "#9D7DF2", amber: "#FE9A00", "amber-ink": "#FFB900", green: "#9D7DF2", desk: "#272139" },
    orb: { light: { canvas: "#F8F7F9", accent: "#7253B9", action: "#A82C87" }, dark: { canvas: "#1D1929", accent: "#9D7DF2", action: "#F099D8" } },
  },
] as const satisfies readonly Readonly<{ id: string; label: string; light: Palette; dark: Palette; orb: Readonly<Record<ThemeMode, Orb>> }>[];

export type AppTheme = (typeof APP_THEMES)[number];
export type AppThemeId = AppTheme["id"];

/** The theme a reader wears until they choose another, in both modes. */
export const DEFAULT_THEME_ID: AppThemeId = "heyday";

/** Decodes a theme id; anything else is null. */
export function parseThemeId(value: unknown): AppThemeId | null {
  return APP_THEMES.find((theme) => theme.id === value)?.id ?? null;
}

export function appTheme(id: AppThemeId): AppTheme {
  const theme = APP_THEMES.find((candidate) => candidate.id === id);
  if (!theme) throw new Error(`no theme ${id}`);
  return theme;
}
